// src/pages/ContactUs.jsx
// SEO-optimised contact page for Dynesourcer
// Target keywords: contact BTL mortgage broker UK, buy-to-let mortgage enquiry,
// Dynesourcer contact, mortgage sourcing platform support, lender listing enquiry

import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
  Search,
  Upload,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const CONTACT_HERO_SRC = "/Contact_us.webp";
const TEAM_CONTACTS = [
  {
    name: "Matt Mander",
    role: "Director",
    phone: "07453 277829",
    email: "matt@dynamiccf.co.uk",
    image: "/Matt_FINAL.webp",
  },
  {
    name: "Amit Patel",
    role: "Finance Broker",
    phone: "07763092290",
    email: "amit@dynamiccf.co.uk",
    image: "/Amit_FINAL.png",
  },
  {
    name: "Josh Mander",
    role: "Finance Broker",
    phone: "07477473473",
    email: "josh@dynamiccf.co.uk",
    image: "/Josh_FINAL.webp",
  },
  {
    name: "Khurram Saif",
    role: "IT Support",
    phone: "07578585132",
    email: "Khurram@dynamiccf.co.uk",
    image: "/Khurram_FINAL.png?v=19",
  },
];

export default function ContactUs() {
  React.useEffect(() => {
    document.title = "Contact Dynesourcer | BTL Mortgage Sourcing Enquiries | Northampton";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Get in touch with the Dynesourcer team. Enquiries about buy-to-let mortgage sourcing, online applications, lender product listings and broker support. Email, phone or visit us in Northampton."
      );
    }
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = CONTACT_HERO_SRC;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      {/* ── Hero ── */}
      <section
        className="relative h-[420px] w-full overflow-hidden"
        aria-label="Contact Dynesourcer — BTL mortgage sourcing enquiries"
      >
        <h1 className="sr-only">
          Contact Dynesourcer — Buy-to-Let Mortgage Sourcing &amp; Lender Enquiries
        </h1>
        <img
          src={CONTACT_HERO_SRC}
          alt="Contact the Dynesourcer team for buy-to-let mortgage sourcing enquiries"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-sky-900/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            Get in touch
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
            Contact Dynesourcer
          </h2>
          <p className="mt-4 max-w-2xl text-white/90 text-lg leading-relaxed">
            Questions about buy-to-let mortgage sourcing, your application, or listing
            products as a lender? Our team is here to help.
          </p>
        </div>
      </section>

      {/* ── Team contacts ── */}
      <section
        className="py-16 md:py-20 bg-slate-50"
        aria-label="Dynesourcer team contact details"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">Meet Our Team</h2>
            <p className="mt-3 text-slate-600">
              Speak directly with our specialist broker team.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM_CONTACTS.map((member) => (
              <article
                key={member.email}
                className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.01] hover:shadow-xl"
              >
                <div className="h-64 w-full overflow-hidden rounded-xl">
                  <img
                    src={member.image}
                    alt={`${member.name} profile`}
                    className="h-full w-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="px-1 pt-4 pb-2">
                  <h3 className="text-2xl font-black text-slate-900 leading-tight">{member.name}</h3>
                  <p className="text-base text-sky-800">{member.role}</p>
                  {member.phone && (
                    <a
                      href={`tel:${member.phone.replace(/\s+/g, "")}`}
                      className="block text-sm text-sky-800 hover:text-sky-600"
                    >
                      {member.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${member.email}`}
                    className="block text-sm text-sky-800 underline underline-offset-2 hover:text-sky-600 break-all"
                  >
                    {member.email}
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reason to contact cards ── */}
      <div style={{ backgroundColor: "rgb(2 132 199)" }} className="text-white py-3">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm font-semibold">
            {[
              "BTL mortgage sourcing enquiries",
              "Application support",
              "Lender product listing",
              "General broker enquiries",
            ].map((item) => (
              <span key={item} className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 flex-shrink-0" /> {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main contact section ── */}
      <section
        className="py-16 md:py-24 bg-white"
        aria-label="Dynesourcer contact details and office location"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left — contact details */}
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-3">
                Speak to the Dynesourcer team
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you're a landlord sourcing a buy-to-let mortgage, an investor with
                a question about your application, or a lender looking to list products —
                get in touch and we'll come back to you promptly.
              </p>

              {/* Contact details */}
              <div className="space-y-5 mb-10">
                <a
                  href="mailto:admin@dynesourcer.co.uk"
                  className="flex items-start gap-4 p-5 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-sky-200 transition-all group"
                >
                  <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 group-hover:bg-sky-100 transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Email us</p>
                    <p className="font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
                      admin@dynesourcer.co.uk
                    </p>
                    <p className="text-sm text-slate-500 mt-0.5">We aim to respond within one business day</p>
                  </div>
                </a>

                <a
                  href="tel:02039786758"
                  className="flex items-start gap-4 p-5 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-sky-200 transition-all group"
                >
                  <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 group-hover:bg-sky-100 transition-colors">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Call us</p>
                    <p className="font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
                      020 3978 6758
                    </p>
                    <p className="text-sm text-slate-500 mt-0.5">Monday – Friday, 9am – 5:00pm</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-5 rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Our office</p>
                    <p className="font-bold text-slate-900">
                      Dynesourcer Ltd.
                      <br />
                      c/o Dynamic Commercial Finance Ltd.
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">
                      Ground Floor, 3 Brooklands Court<br />
                      Kettering Venture Park<br />
                      Kettering, Northamptonshire<br />
                      NN15 6FD<br />
                      United Kingdom
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex-shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Office hours</p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      <span className="font-semibold">Monday – Friday:</span> 9:00am – 5:00pm<br />
                      <span className="font-semibold">Saturday – Sunday:</span> Closed
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      You can source and enquire online 24/7 via the platform
                    </p>
                  </div>
                </div>
              </div>

              {/* What to contact us about */}
              <div className="rounded-xl bg-sky-50 border border-sky-100 p-6">
                <h3 className="font-bold text-slate-900 mb-4">What can we help you with?</h3>
                <ul className="space-y-3">
                  {[
                    { icon: <Search className="h-4 w-4" />, label: "Questions about sourcing a buy-to-let mortgage" },
                    { icon: <MessageCircle className="h-4 w-4" />, label: "Support with your online application or fact-find" },
                    { icon: <Upload className="h-4 w-4" />, label: "Lender enquiries about listing BTL mortgage products" },
                    { icon: <Mail className="h-4 w-4" />, label: "General enquiries about the Dynesourcer platform" },
                  ].map((it) => (
                    <li key={it.label} className="flex items-center gap-3 text-sm text-slate-700">
                      <span className="text-sky-500 flex-shrink-0">{it.icon}</span>
                      {it.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right — map */}
            <div>
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-4">
                  <p className="font-bold text-slate-900 text-sm">Dynesourcer — Kettering</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Ground Floor, 3 Brooklands Court, Kettering Venture Park, NN15 6FD
                  </p>
                </div>
                <iframe
                  title="Dynesourcer office location — Brooklands Court, Kettering"
                  src="https://www.google.com/maps?q=Ground+Floor,+3+Brooklands+Court,+Kettering+Venture+Park,+Kettering,+NN15+6FD,+United+Kingdom&output=embed"
                  className="w-full h-[420px] md:h-[520px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section
        className="py-14 text-center text-white"
        style={{ backgroundColor: "rgb(2 132 199)" }}
        aria-label="Start sourcing buy-to-let mortgages or go to lender portal"
      >
        <h2 className="text-2xl md:text-3xl font-black">
          Not ready to call? Start sourcing online now
        </h2>
        <p className="mt-2 text-white/90 max-w-xl mx-auto">
          Compare buy-to-let mortgage products matched to your eligibility — free to source
          and enquire, no phone call needed.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/sourcing/buy-to-let-mortgage-sourcing"
            className="inline-flex items-center gap-2 bg-white text-sky-700 font-bold px-7 py-3.5 rounded-lg hover:bg-slate-100 transition-colors shadow-lg"
          >
            <Search className="h-5 w-5" /> Source BTL mortgages
          </Link>
          <Link
            to="/lenders"
            className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-white/25 transition-colors"
          >
            Lender portal <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}